import { Link } from 'react-router-dom';
import type { Article } from '@/mocks/articles';

interface ArticleCardProps {
  article: Article;
  variant?: 'default' | 'hero' | 'compact';
}

export default function ArticleCard({ article, variant = 'default' }: ArticleCardProps) {
  if (variant === 'hero') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group relative block w-full h-[420px] md:h-[520px] rounded-lg overflow-hidden cursor-pointer"
      >
        <img
          src={article.coverImage}
          alt={article.title}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-black/10"></div>
        <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
          {article.isExclusive && (
            <span className="inline-block px-2.5 py-1 text-xs font-medium rounded-full bg-secondary-500 text-background-950 mb-3 whitespace-nowrap">
              独家爆料
            </span>
          )}
          <h2 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-white mb-3 leading-tight line-clamp-2">
            {article.title}
          </h2>
          <p className="text-sm md:text-base text-white/70 line-clamp-2 mb-4 max-w-2xl">
            {article.excerpt}
          </p>
          <div className="flex items-center gap-4 text-xs text-white/50">
            <span className="whitespace-nowrap">
              <i className="ri-user-line mr-1"></i>{article.author}
            </span>
            <span className="whitespace-nowrap">
              <i className="ri-calendar-line mr-1"></i>{article.publishDate}
            </span>
            <span className="whitespace-nowrap">
              <i className="ri-eye-line mr-1"></i>{(article.readCount / 10000).toFixed(1)}万
            </span>
          </div>
        </div>
      </Link>
    );
  }

  if (variant === 'compact') {
    return (
      <Link
        to={`/article/${article.id}`}
        className="group flex gap-3 p-3 rounded-lg hover:bg-background-200/50 transition-colors cursor-pointer"
      >
        <div className="w-20 h-20 md:w-24 md:h-24 rounded-lg overflow-hidden flex-shrink-0">
          <img
            src={article.coverImage}
            alt={article.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-medium text-foreground-900 line-clamp-2 mb-1 group-hover:text-primary-500 transition-colors">
            {article.title}
          </h4>
          <div className="flex items-center gap-3 text-xs text-foreground-400 whitespace-nowrap">
            <span>{article.categoryName}</span>
            <span>{article.publishDate}</span>
          </div>
        </div>
      </Link>
    );
  }

  return (
    <Link
      to={`/article/${article.id}`}
      className="group block rounded-lg overflow-hidden bg-background-100 border border-background-300/30 hover:border-primary-500/30 transition-all duration-300 cursor-pointer"
    >
      <div className="relative w-full h-48 md:h-52 overflow-hidden">
        <img
          src={article.coverImage}
          alt={article.title}
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
          loading="lazy"
        />
        {article.isHot && (
          <span className="absolute top-3 left-3 px-2 py-0.5 text-xs font-medium rounded-full bg-primary-500 text-background-50 whitespace-nowrap">
            热门
          </span>
        )}
        {article.isExclusive && (
          <span className="absolute top-3 right-3 px-2 py-0.5 text-xs font-medium rounded-full bg-secondary-500 text-background-950 whitespace-nowrap">
            独家
          </span>
        )}
      </div>
      <div className="p-4">
        <span className="inline-block text-xs text-accent-500 font-medium mb-2 whitespace-nowrap">
          {article.categoryName}
        </span>
        <h3 className="font-heading text-base md:text-lg font-semibold text-foreground-950 line-clamp-2 mb-2 group-hover:text-primary-500 transition-colors">
          {article.title}
        </h3>
        <p className="text-sm text-foreground-500 line-clamp-2 mb-3">
          {article.excerpt}
        </p>
        <div className="flex items-center justify-between text-xs text-foreground-400">
          <span className="whitespace-nowrap">{article.author} · {article.publishDate}</span>
          <span className="whitespace-nowrap">
            <i className="ri-eye-line mr-1"></i>{(article.readCount / 10000).toFixed(1)}万
          </span>
        </div>
      </div>
    </Link>
  );
}